import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  listAISources, listAISourceProjects, fetchAISourceFile,
  deleteAISource, purgeAISources, safeErrorMessage,
} from '../services/api';
import { useToast } from '../App';
import Pagination from '../components/Pagination';
import {
  HiOutlineRefresh, HiOutlineDownload, HiOutlineSearch, HiOutlineTrash,
  HiOutlineEye, HiOutlineX,
} from 'react-icons/hi';
import { FiPaperclip, FiFile, FiFileText, FiGrid, FiDatabase } from 'react-icons/fi';

const PAGE_SIZE = 25;

// Couleur et icone par famille de format, pour reperer un fichier d'un coup d'oeil.
const FORMATS = {
  '.pdf': { label: 'PDF', color: '#dc2626', Icone: FiFile },
  '.docx': { label: 'Word', color: '#2563eb', Icone: FiFileText },
  '.xlsx': { label: 'Excel', color: '#16a34a', Icone: FiGrid },
  '.xlsm': { label: 'Excel', color: '#16a34a', Icone: FiGrid },
  '.csv': { label: 'CSV', color: '#0891b2', Icone: FiGrid },
  '.txt': { label: 'Texte', color: '#64748b', Icone: FiFileText },
  '.md': { label: 'Markdown', color: '#64748b', Icone: FiFileText },
};

const formatInfo = (ext) => FORMATS[ext] || { label: ext || '?', color: '#64748b', Icone: FiFile };

const formatTaille = (octets) => {
  if (!octets) return '0 o';
  if (octets < 1024) return `${octets} o`;
  if (octets < 1024 * 1024) return `${(octets / 1024).toFixed(0)} Ko`;
  return `${(octets / (1024 * 1024)).toFixed(1)} Mo`;
};

const formatDate = (iso) => {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

export default function AISourcesPage() {
  const addToast = useToast();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalSize, setTotalSize] = useState(0);
  const [retention, setRetention] = useState(null);
  const [projets, setProjets] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filtreProjet, setFiltreProjet] = useState('');
  const [recherche, setRecherche] = useState('');
  const [rechercheActive, setRechercheActive] = useState('');

  const [apercu, setApercu] = useState(null);   // { item, url } | { item, url: null }
  const [chargementApercu, setChargementApercu] = useState(false);
  const [aSupprimer, setASupprimer] = useState(null);
  const [suppression, setSuppression] = useState(false);

  // Conserve l'URL blob courante pour la revoquer : sans cela, chaque apercu
  // laisserait le fichier en memoire jusqu'au rechargement de la page.
  const urlCourante = useRef(null);

  const libereApercu = useCallback(() => {
    if (urlCourante.current) {
      URL.revokeObjectURL(urlCourante.current);
      urlCourante.current = null;
    }
  }, []);

  useEffect(() => libereApercu, [libereApercu]);

  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE };
      if (filtreProjet) params.project_id = filtreProjet;
      if (rechercheActive) params.q = rechercheActive;
      const { data } = await listAISources(params);
      setItems(data.items || []);
      setTotal(data.total || 0);
      setTotalSize(data.total_size || 0);
      setRetention(data.retention_days);
    } catch (err) {
      addToast('error', safeErrorMessage(err, 'Chargement des documents impossible'));
    } finally {
      setLoading(false);
    }
  }, [page, filtreProjet, rechercheActive, addToast]);

  useEffect(() => { charger(); }, [charger]);

  useEffect(() => {
    listAISourceProjects()
      .then(({ data }) => setProjets(data || []))
      .catch(() => { /* le filtre reste vide, la liste fonctionne quand meme */ });
  }, []);

  const recupereBlob = async (item) => {
    const { data } = await fetchAISourceFile(item.id);
    // Le type est impose par la fiche et non devine par le navigateur.
    return URL.createObjectURL(new Blob([data], { type: item.content_type }));
  };

  const ouvrirApercu = async (item) => {
    if (!item.available) {
      addToast('error', "Ce fichier n'est plus present sur le serveur.");
      return;
    }
    setChargementApercu(true);
    try {
      const url = await recupereBlob(item);
      libereApercu();
      urlCourante.current = url;
      setApercu({ item, url });
    } catch (err) {
      addToast('error', safeErrorMessage(err, "Ouverture impossible"));
    } finally {
      setChargementApercu(false);
    }
  };

  const fermerApercu = () => {
    libereApercu();
    setApercu(null);
  };

  const telecharger = async (item) => {
    if (!item.available) {
      addToast('error', "Ce fichier n'est plus present sur le serveur.");
      return;
    }
    try {
      const url = await recupereBlob(item);
      const a = document.createElement('a');
      a.href = url;
      a.download = item.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      addToast('error', safeErrorMessage(err, 'Telechargement impossible'));
    }
  };

  const confirmerSuppression = async () => {
    if (!aSupprimer) return;
    setSuppression(true);
    try {
      const { data } = await deleteAISource(aSupprimer.id);
      addToast('success', data.message || 'Document supprime');
      setASupprimer(null);
      if (items.length === 1 && page > 1) setPage(page - 1); else charger();
    } catch (err) {
      addToast('error', safeErrorMessage(err, 'Suppression impossible'));
    } finally {
      setSuppression(false);
    }
  };

  const lancerPurge = async () => {
    try {
      const { data } = await purgeAISources();
      addToast('success',
        `Purge effectuee : ${data.expired} archive(s) expiree(s), ${data.orphans} fichier(s) orphelin(s).`);
      charger();
    } catch (err) {
      addToast('error', safeErrorMessage(err, 'Purge impossible'));
    }
  };

  const soumettreRecherche = (e) => {
    e.preventDefault();
    setRechercheActive(recherche.trim());
    setPage(1);
  };

  const reinitialiser = () => {
    setRecherche('');
    setRechercheActive('');
    setFiltreProjet('');
    setPage(1);
  };

  return (
    <div>
      <div className="page-header">
        <h2>
          <FiPaperclip style={{ marginRight: 8 }} />
          Documents sources IA
        </h2>
        <div className="d-flex gap-2">
          <button className="btn btn-outline-secondary btn-sm" onClick={charger} disabled={loading}>
            <HiOutlineRefresh style={{ marginRight: 4 }} />
            Actualiser
          </button>
          <button className="btn btn-outline-danger btn-sm" onClick={lancerPurge}>
            <HiOutlineTrash style={{ marginRight: 4 }} />
            Purger les expires
          </button>
        </div>
      </div>

      <p className="text-muted small">
        Fichiers envoyes pour generer des exigences et des cas de test. Cette page est
        reservee au super administrateur.
        {retention ? ` Les archives de plus de ${retention} jours sont supprimees automatiquement.` : ''}
      </p>

      {/* Chiffres cles */}
      <div className="row g-3 mb-3">
        <div className="col-sm-4">
          <div className="card h-100">
            <div className="card-body py-3">
              <div className="text-muted small">Documents archives</div>
              <div className="h4 mb-0">{total}</div>
            </div>
          </div>
        </div>
        <div className="col-sm-4">
          <div className="card h-100">
            <div className="card-body py-3">
              <div className="text-muted small">Espace occupe</div>
              <div className="h4 mb-0">{formatTaille(totalSize)}</div>
            </div>
          </div>
        </div>
        <div className="col-sm-4">
          <div className="card h-100">
            <div className="card-body py-3">
              <div className="text-muted small">Projets concernes</div>
              <div className="h4 mb-0">{projets.length}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filtres */}
      <div className="card mb-3">
        <div className="card-body py-3">
          <form className="row g-2 align-items-end" onSubmit={soumettreRecherche}>
            <div className="col-sm-4">
              <label className="form-label small mb-1">Projet</label>
              <select
                className="form-select form-select-sm"
                value={filtreProjet}
                onChange={(e) => { setFiltreProjet(e.target.value); setPage(1); }}
              >
                <option value="">Tous les projets</option>
                {projets.map((p) => (
                  <option key={p.id} value={p.id}>{p.name} ({p.total})</option>
                ))}
              </select>
            </div>
            <div className="col-sm-5">
              <label className="form-label small mb-1">Nom du fichier</label>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Rechercher..."
                value={recherche}
                onChange={(e) => setRecherche(e.target.value)}
              />
            </div>
            <div className="col-sm-3 d-flex gap-2">
              <button type="submit" className="btn btn-primary btn-sm flex-grow-1">
                <HiOutlineSearch style={{ marginRight: 4 }} />
                Chercher
              </button>
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={reinitialiser}>
                Reinitialiser
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Liste */}
      <div className="card">
        <div className="table-responsive">
          <table className="table table-hover mb-0 align-middle">
            <thead>
              <tr>
                <th>Fichier</th>
                <th>Projet</th>
                <th>Envoye par</th>
                <th className="text-end">Taille</th>
                <th className="text-end">Appels</th>
                <th>Date</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan="7" className="text-center py-4 text-muted">Chargement...</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan="7" className="text-center py-5 text-muted">
                    <FiDatabase size={32} className="mb-2 d-block mx-auto opacity-50" />
                    Aucun document archive.
                    <div className="small">
                      Les fichiers envoyes lors des prochaines generations apparaitront ici.
                    </div>
                  </td>
                </tr>
              )}
              {!loading && items.map((item) => {
                const { label, color, Icone } = formatInfo(item.extension);
                return (
                  <tr key={item.id}>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <Icone style={{ color, flexShrink: 0 }} />
                        <div className="text-truncate" style={{ maxWidth: 320 }} title={item.filename}>
                          {item.filename}
                          {!item.available && (
                            <span className="badge bg-secondary ms-2">fichier absent</span>
                          )}
                        </div>
                        <span className="badge" style={{ background: color, flexShrink: 0 }}>{label}</span>
                      </div>
                    </td>
                    <td className="small">{item.project ? item.project.name : <span className="text-muted">-</span>}</td>
                    <td className="small">
                      {item.uploaded_by ? (
                        <>
                          <div>{item.uploaded_by.full_name}</div>
                          <div className="text-muted">{item.uploaded_by.email}</div>
                        </>
                      ) : <span className="text-muted">compte supprime</span>}
                    </td>
                    <td className="text-end small">{formatTaille(item.size_bytes)}</td>
                    <td className="text-end small" title={`${item.chars.toLocaleString('fr-FR')} caracteres extraits`}>
                      {item.segments}
                    </td>
                    <td className="small">{formatDate(item.created_at)}</td>
                    <td className="text-end text-nowrap">
                      {item.previewable && (
                        <button
                          className="btn btn-sm btn-outline-primary me-1"
                          onClick={() => ouvrirApercu(item)}
                          disabled={!item.available || chargementApercu}
                          title="Voir le document"
                        >
                          <HiOutlineEye />
                        </button>
                      )}
                      <button
                        className="btn btn-sm btn-outline-secondary me-1"
                        onClick={() => telecharger(item)}
                        disabled={!item.available}
                        title="Telecharger"
                      >
                        <HiOutlineDownload />
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => setASupprimer(item)}
                        title="Supprimer definitivement"
                      >
                        <HiOutlineTrash />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <Pagination
        currentPage={page}
        totalItems={total}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
      />

      {/* Apercu du document */}
      {apercu && (
        <>
          <div className="modal-backdrop fade show" onClick={fermerApercu}></div>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog">
            <div className="modal-dialog modal-xl modal-dialog-centered">
              <div className="modal-content" style={{ height: '88vh' }}>
                <div className="modal-header">
                  <h5 className="modal-title text-truncate" title={apercu.item.filename}>
                    {apercu.item.filename}
                  </h5>
                  <div className="d-flex align-items-center gap-2">
                    <button className="btn btn-sm btn-outline-secondary"
                            onClick={() => telecharger(apercu.item)}>
                      <HiOutlineDownload style={{ marginRight: 4 }} />
                      Telecharger
                    </button>
                    <button className="btn btn-sm btn-outline-secondary" onClick={fermerApercu}>
                      <HiOutlineX />
                    </button>
                  </div>
                </div>
                <div className="modal-body p-0" style={{ overflow: 'hidden' }}>
                  <iframe
                    src={apercu.url}
                    title={apercu.item.filename}
                    style={{ width: '100%', height: '100%', border: 0 }}
                  />
                </div>
                <div className="modal-footer py-2 small text-muted">
                  {apercu.item.project ? `Projet : ${apercu.item.project.name}` : ''}
                  {apercu.item.uploaded_by ? ` · Envoye par ${apercu.item.uploaded_by.email}` : ''}
                  {` · ${formatTaille(apercu.item.size_bytes)}`}
                  {` · ${apercu.item.segments} appel(s) au modele`}
                  {` · ${apercu.item.chars.toLocaleString('fr-FR')} caracteres extraits`}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Confirmation de suppression */}
      {aSupprimer && (
        <>
          <div className="modal-backdrop fade show" onClick={() => setASupprimer(null)}></div>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Supprimer ce document ?</h5>
                </div>
                <div className="modal-body">
                  <p className="mb-2">
                    <strong>{aSupprimer.filename}</strong>
                  </p>
                  <p className="text-muted small mb-0">
                    Le fichier sera efface du serveur, sans retour possible. Les exigences et
                    les cas de test deja generes a partir de ce document ne sont pas touches.
                  </p>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-outline-secondary btn-sm"
                          onClick={() => setASupprimer(null)} disabled={suppression}>
                    Annuler
                  </button>
                  <button className="btn btn-danger btn-sm"
                          onClick={confirmerSuppression} disabled={suppression}>
                    {suppression ? 'Suppression...' : 'Supprimer'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
